import React from "react";
import { useTranslation } from "react-i18next";
import images from "../../constant/images";
import Header from "../../layouts/header";

export const RandoAventure = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-16 mb-20 bg-white overflow-hidden">
      <Header image={images.rando_aventure} title={t("randoAventure.title")} />

      {/* Section 1: Rando & 4x4 */}
      <div className="container mx-auto px-4 sm:px-8 lg:px-[8rem] flex flex-col gap-12">
        {/* Centered Top Content */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <h2 className="text-4xl md:text-5xl font-bold text-[#00573f] custom-font">
            {t("randoAventure.section1.title")}
          </h2>
          <p className="text-xl font-bold text-black uppercase tracking-widest">
            {t("randoAventure.section1.intro")}
          </p>
          <p className="text-gray-600 text-lg leading-relaxed karla-font">
            {t("randoAventure.section1.description")}
          </p>
        </div>

        {/* Side-by-Side Content */}
        <div className="flex flex-col lg:flex-row items-stretch gap-12">
          <div className="w-full lg:w-[45%] rounded-[2.5rem] overflow-hidden shadow-2xl transition-all duration-700 hover:scale-[1.02]">
            <img 
              src={images.rando_aventure} 
              alt="Rando & 4x4" 
              className="w-full h-full min-h-[500px] object-cover"
            />
          </div>
          <div className="w-full lg:w-[55%] flex">
            <div className="relative bg-white rounded-[2.5rem] p-10 md:p-14 shadow-[0_22px_70px_4px_rgba(0,0,0,0.05)] border border-gray-50 overflow-hidden flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-10">
                <span className="inline-flex items-center justify-center bg-[#00573f] text-white text-[10px] font-bold px-4 py-1.5 rounded-full uppercase tracking-[0.2em]">
                  4x4 Expeditions
                </span>
                <span className="h-[1px] flex-grow bg-gray-100"></span>
              </div>
              
              <h3 className="text-3xl font-bold mb-6 text-black tracking-tight">
                {t("randoAventure.section1.subTitle")}
              </h3>
              
              <p className="text-gray-500 text-lg leading-relaxed mb-10 karla-font italic">
                "{t("randoAventure.section1.content")}"
              </p>
              
              <div className="pt-10 border-t border-gray-100">
                <h4 className="text-xs font-black uppercase tracking-[0.25em] text-nav_gold mb-8 flex items-center gap-3">
                  Selection of Experiences
                </h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                  {t("randoAventure.section1.items", { returnObjects: true })?.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 group">
                      <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-gray-50 flex items-center justify-center transition-colors group-hover:bg-[#00573f]/10">
                        <svg className="w-4 h-4 text-[#00573f]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-sm font-medium text-gray-600 transition-colors group-hover:text-black">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Rando & Rafting */}
      <div className="container mx-auto px-4 sm:px-8 lg:px-[8rem] flex flex-col gap-12 mt-16">
        {/* Centered Top Content */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <h2 className="text-4xl md:text-5xl font-bold text-[#00573f] custom-font">
            {t("randoAventure.section2.title")}
          </h2>
          <p className="text-xl font-bold text-black uppercase tracking-widest">
            {t("randoAventure.section2.intro")}
          </p>
          <p className="text-gray-600 text-lg leading-relaxed karla-font">
            {t("randoAventure.section2.description")}
          </p>
        </div>

        {/* Side-by-Side Content */}
        <div className="flex flex-col lg:flex-row-reverse items-stretch gap-12">
          <div className="w-full lg:w-[45%] rounded-[2.5rem] overflow-hidden transition-all duration-700 hover:scale-[1.02]">
            <img 
              src={images.rando_aventure_1} 
              alt="Rando & Rafting" 
              className="w-full h-full min-h-[500px] object-cover"
            />
          </div>
          <div className="w-full lg:w-[55%]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 h-full">
              <div className="relative bg-white rounded-[2.5rem] p-10 md:p-12 border border-gray-100 overflow-hidden h-full">
                <div className="flex items-center gap-4 mb-8">
                  <span className="inline-flex items-center justify-center bg-[#00573f] text-white text-[10px] font-bold px-4 py-1.5 rounded-full uppercase tracking-[0.2em]">
                    Wild Rivers
                  </span>
                  <span className="h-[1px] flex-grow bg-gray-100"></span>
                </div>
                
                <h3 className="text-2xl font-bold mb-4 text-black">
                  {t("randoAventure.section2.exploreTitle")}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">
                  {t("randoAventure.section2.exploreContent")}
                </p>
                
                <div className="space-y-4 pt-6 border-t border-gray-100">
                  {t("randoAventure.section2.items", { returnObjects: true })?.map((item, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-nav_gold"></div>
                      <span className="text-sm font-semibold text-gray-700 uppercase tracking-tight">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-[#0a0a0a] text-white p-10 md:p-12 rounded-[2.5rem] border border-white/5 relative overflow-hidden flex flex-col justify-between">
                <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-[#00573f]/20 to-transparent pointer-events-none"></div>
                
                <div>
                  <h3 className="text-2xl font-bold mb-2 text-white">
                    {t("randoAventure.section2.richTitle")}
                  </h3>
                  <p className="text-xs text-gray-500 uppercase tracking-widest mb-8 text-gray-400">
                    Sensory Discovery
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {t("randoAventure.section2.richItems", { returnObjects: true })?.map((item, index) => (
                      <div key={index} className="p-4 rounded-2xl bg-white/5 border border-white/10 flex flex-col gap-2">
                        <span className="text-white font-black text-xl">0{index + 1}</span>
                        <span className="text-[11px] font-bold uppercase tracking-tighter text-gray-400 leading-tight">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-10 pt-8 border-t border-white/10">
                  <p className="text-sm font-medium italic text-white leading-relaxed">
                    {t("randoAventure.section2.conclusion")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
